import { useState, useEffect, useCallback } from 'react';

export interface JobListing {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
  icon: string;
}

const STORAGE_KEY = 'tmt_job_listings';

const defaultJobs: JobListing[] = [
  {
    id: 'senior-backend',
    title: 'Senior Backend Engineer',
    department: 'Engineering',
    location: 'Beirut, Lebanon',
    type: 'Full-time',
    description: 'Design and build the core transaction processing engine powering TMT\'s cross-border payment infrastructure. You will work on distributed systems handling millions of transactions daily across 14+ corridors, with a focus on consistency, idempotency, and sub-second latency.',
    requirements: [
      '7+ years of backend development experience with Node.js, Go, or Java',
      'Deep understanding of distributed systems, message queues, and event-driven architectures',
      'Experience with financial transaction processing or payment gateways is strongly preferred',
      'Proficiency with PostgreSQL, Redis, and cloud infrastructure (AWS or GCP)',
      'Knowledge of PCI-DSS compliance and secure coding practices',
    ],
    icon: 'ri-code-s-slash-line',
  },
  {
    id: 'compliance-officer',
    title: 'Regulatory Compliance Officer — MENA',
    department: 'Legal & Compliance',
    location: 'Dubai, UAE',
    type: 'Full-time',
    description: 'Lead TMT\'s regulatory compliance program across the MENA region. You will manage relationships with central banks and financial regulators, oversee AML/CFT frameworks, and ensure that TMT\'s operations remain fully aligned with evolving regional regulations.',
    requirements: [
      '8+ years of compliance experience in financial services or fintech',
      'Deep knowledge of MENA financial regulations, including UAE Central Bank, SAMA, and QCB frameworks',
      'CAMS, ICA, or equivalent AML certification required',
      'Experience managing regulatory examinations and external audits',
      'Fluent Arabic and English required; French is a strong plus',
    ],
    icon: 'ri-scales-3-line',
  },
  {
    id: 'product-manager',
    title: 'Product Manager — Digital Cards',
    department: 'Product',
    location: 'Beirut, Lebanon',
    type: 'Full-time',
    description: 'Own the product roadmap for TMT\'s virtual and physical Visa card program, including spend controls, loyalty integrations, and the premium cardholder experience. You will work closely with Visa, issuing banks, and our engineering team to deliver a world-class card product.',
    requirements: [
      '5+ years of product management experience in fintech or payments',
      'Experience launching or managing card programs (debit, credit, or prepaid)',
      'Strong data analysis skills with SQL and product analytics tools',
      'Excellent stakeholder management and communication skills',
      'Experience with card network integrations (Visa, Mastercard) is a strong advantage',
    ],
    icon: 'ri-bank-card-line',
  },
  {
    id: 'ux-designer',
    title: 'Senior UX Designer',
    department: 'Design',
    location: 'Remote (EMEA)',
    type: 'Full-time',
    description: 'Design intuitive, elegant, and accessible financial experiences for TMT\'s mobile and web platforms. You will lead user research across diverse markets, design end-to-end flows for complex financial operations, and contribute to TMT\'s design system.',
    requirements: [
      '5+ years of UX design experience with a strong portfolio of shipped products',
      'Experience designing for fintech, banking, or complex transactional interfaces',
      'Proficiency with Figma, prototyping tools, and user research methodologies',
      'Understanding of accessibility standards and inclusive design practices',
      'Experience designing for Arabic (RTL) and multi-language interfaces is a plus',
    ],
    icon: 'ri-palette-line',
  },
  {
    id: 'data-scientist',
    title: 'Data Scientist — Fraud & Risk',
    department: 'Data & AI',
    location: 'Beirut, Lebanon',
    type: 'Full-time',
    description: 'Build and deploy machine learning models that protect TMT users from fraud while minimizing false positives. You will develop real-time scoring systems, behavioral analytics, and anomaly detection pipelines that process millions of events daily.',
    requirements: [
      'MS or PhD in Computer Science, Statistics, Mathematics, or a related quantitative field',
      '4+ years of applied ML experience, preferably in fraud detection, risk modeling, or financial crime',
      'Strong Python skills with experience in TensorFlow, PyTorch, or scikit-learn',
      'Experience with real-time streaming data (Kafka, Flink) and feature engineering at scale',
      'Ability to communicate complex technical concepts to non-technical stakeholders',
    ],
    icon: 'ri-brain-line',
  },
  {
    id: 'customer-success',
    title: 'Customer Success Manager — Africa',
    department: 'Operations',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    description: 'Lead TMT\'s customer success function across African markets. You will build and manage a multilingual support team, drive customer satisfaction metrics, and act as the voice of African users within TMT\'s product development process.',
    requirements: [
      '5+ years in customer success, support operations, or account management',
      'Experience in fintech, mobile money, or financial services in African markets',
      'Fluency in English and Swahili required; French is a strong advantage',
      'Data-driven approach to measuring and improving customer satisfaction',
      'Willingness to travel within Africa up to 25% of the time',
    ],
    icon: 'ri-customer-service-2-line',
  },
];

function loadFromStorage(): JobListing[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== null) {
      const parsed = JSON.parse(raw) as JobListing[];
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // ignore parse errors
  }
  return [...defaultJobs];
}

function saveToStorage(jobs: JobListing[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
}

export function useJobListings() {
  const [jobs, setJobs] = useState<JobListing[]>(() => loadFromStorage());
  const [departments, setDepartments] = useState<string[]>(['All Departments']);

  // Sync with localStorage — catches changes from admin panel (same tab + cross-tab)
  useEffect(() => {
    const sync = () => setJobs(loadFromStorage());
    window.addEventListener('storage', sync);
    // Also sync on focus — covers same-tab navigation back from admin
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  useEffect(() => {
    const depts = Array.from(new Set(jobs.map((j) => j.department)));
    setDepartments(['All Departments', ...depts]);
  }, [jobs]);

  const addJob = useCallback((job: Omit<JobListing, 'id'>) => {
    const newJob: JobListing = {
      ...job,
      id: `job-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    };
    setJobs((prev) => {
      const next = [newJob, ...prev];
      saveToStorage(next);
      return next;
    });
  }, []);

  const updateJob = useCallback((id: string, updates: Partial<JobListing>) => {
    setJobs((prev) => {
      const next = prev.map((j) => (j.id === id ? { ...j, ...updates } : j));
      saveToStorage(next);
      return next;
    });
  }, []);

  const deleteJob = useCallback((id: string) => {
    setJobs((prev) => {
      const next = prev.filter((j) => j.id !== id);
      saveToStorage(next);
      return next;
    });
  }, []);

  const resetToDefault = useCallback(() => {
    setJobs([...defaultJobs]);
    saveToStorage([...defaultJobs]);
  }, []);

  return {
    jobs,
    departments,
    addJob,
    updateJob,
    deleteJob,
    resetToDefault,
  };
}